import { MutationCache, QueryClient } from "@tanstack/react-query";

import { ApiError } from "./errors";

// Transient 4xx statuses that should be retried (vs. logical errors like 400/404).
const RETRYABLE_CLIENT_STATUSES = new Set([408, 425, 429]);

interface QueryClientOptions {
  /** Called on every mutation error. Injected by the app shell to show a toast
   *  (api/ may not import shared/, so the toast call is passed in). */
  onMutationError?: (error: unknown) => void;
}

export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.meta?.silentError) return;
        options.onMutationError?.(error);
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // 4xx ApiError: only retry transient statuses (timeout, rate limit).
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            if (!RETRYABLE_CLIENT_STATUSES.has(error.status)) return false;
          }
          // 5xx, retryable 4xx, and network errors: cap at 2 retries.
          return failureCount < 2;
        },
        // Operators trigger explicit refresh and mutations invalidate caches
        // via queryClient; we suppress auto-refetch on focus/reconnect to
        // avoid hammering BE during multi-tab triage.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}
